import { useState } from "react";
import { Alert, App, Button, Card, Descriptions, Form, Input, Modal, Popconfirm, Select, Space, Typography } from "antd";
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { PolicyAcceptanceDto, PolicyVersionDto, RetentionItemDto } from "@audit-plan/shared";
import { ApiError, download } from "../../api/client";
import { usePolicies, usePolicyAcceptances, usePolicyMutations, useRetentionReport, useRunRetention } from "../../api/admin";
import { Pill, fmtD, fmtDT } from "../pills";
import { ResponsiveList } from "../ResponsiveList";
import { useModalProps } from "../useModalProps";
import { Sensitive } from "../Sensitive";

type PolicyForm = { code: string; title: string; audience: "supplier" | "internal"; body: string };

/** Amministrazione → Privacy: informative GDPR (versioni, pubblicazione, accettazioni) e report «Dati oltre i tempi di conservazione». */
export function PrivacyTab() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const modalProps = useModalProps(720);
  const viewProps = useModalProps(720);
  const policies = usePolicies();
  const [filter, setFilter] = useState<string>();
  const acc = usePolicyAcceptances(filter);
  const mut = usePolicyMutations();
  const retention = useRetentionReport();
  const run = useRunRetention();
  const [edit, setEdit] = useState<{ p?: PolicyVersionDto } | null>(null);
  const [view, setView] = useState<PolicyVersionDto | null>(null);
  const [form] = Form.useForm<PolicyForm>();
  const fail = (e: unknown) => void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));

  const open = (p?: PolicyVersionDto) => {
    form.resetFields();
    form.setFieldsValue(p ? { code: p.code, title: p.title, audience: p.audience, body: p.body } : { audience: "supplier" });
    setEdit({ p });
  };
  const submit = async (v: PolicyForm) => {
    try {
      if (edit?.p) await mut.update.mutateAsync({ id: edit.p.id, body: v });
      else await mut.create.mutateAsync(v);
      void message.success(t("saved"));
      setEdit(null);
    } catch (e) {
      fail(e);
    }
  };
  const state = (p: PolicyVersionDto) => (p.active ? <Pill tone="ok">{t("d_polActive")}</Pill> : p.publishedAt ? <Pill tone="mute">{t("d_polHistoric")}</Pill> : <Pill tone="warn">{t("d_polDraft")}</Pill>);
  const kindLabel = (k: RetentionItemDto["kind"]) => t(`d_retKind_${k}`);
  const rr = retention.data;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card
        title={t("d_polTitle")}
        extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => open()}>{t("d_polNew")}</Button>}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ padding: "12px 16px", fontSize: 12.5, opacity: 0.75 }}>{t("d_polHelp")}</div>
        <ResponsiveList<PolicyVersionDto>
          size="small"
          rowKey="id"
          loading={policies.isLoading}
          dataSource={policies.data ?? []}
          pageSize={20}
          card={{
            code: (p) => p.code,
            title: (p) => p.title,
            tags: (p) => <>{state(p)}<Pill tone="info">{t(`d_aud_${p.audience}`)}</Pill></>,
            fields: (p) => [[t("d_polAccepted"), String(p.acceptances ?? 0)]],
            footer: (p) => (
              <Space>
                <Button size="small" onClick={() => setView(p)}>{t("d_polView")}</Button>
              </Space>
            ),
          }}
          columns={[
            { title: t("title"), dataIndex: "title" },
            { title: t("code"), dataIndex: "code", render: (v: string) => <span style={{ fontFamily: "var(--ap-mono)", fontSize: 12 }}>{v}</span> },
            { title: t("d_polAudience"), dataIndex: "audience", render: (v: string) => t(`d_aud_${v}`) },
            { title: t("status"), render: (_, p) => state(p) },
            { title: t("d_polPublished"), dataIndex: "publishedAt", render: (v: string | null) => fmtD(v) },
            { title: t("d_polAccepted"), dataIndex: "acceptances", align: "right" as const },
            {
              title: "",
              render: (_, p) => (
                <Space size={4}>
                  <Button size="small" type="text" onClick={() => setView(p)}>{t("d_polView")}</Button>
                  {!p.publishedAt && <Button size="small" type="text" onClick={() => open(p)}>{t("edit")}</Button>}
                  {!p.publishedAt && (
                    <Popconfirm title={t("d_polPublishConfirm")} okText={t("yes")} cancelText={t("cancel")} onConfirm={() => mut.publish.mutateAsync(p.id).then(() => void message.success(t("d_polPublishedOk"))).catch(fail)}>
                      <Button size="small" type="primary" ghost>{t("d_polPublish")}</Button>
                    </Popconfirm>
                  )}
                  {!p.publishedAt && (
                    <Popconfirm title={t("d_polDeleteConfirm")} okText={t("yes")} cancelText={t("cancel")} onConfirm={() => mut.remove.mutateAsync(p.id).catch(fail)}>
                      <Button size="small" type="text" danger>{t("delete")}</Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Card
        title={t("d_accTitle")}
        extra={
          <Space wrap>
            <Select allowClear size="small" placeholder={t("d_polAll")} style={{ width: 240, maxWidth: "100%" }} value={filter} onChange={setFilter} options={(policies.data ?? []).map((p) => ({ value: p.id, label: `${p.title} (${p.code})` }))} />
            <Button size="small" icon={<DownloadOutlined />} onClick={() => download("/policies/acceptances.xlsx", { policyVersionId: filter })}>{t("exportXlsx")}</Button>
          </Space>
        }
        styles={{ body: { padding: 0 } }}
      >
        <ResponsiveList<PolicyAcceptanceDto>
          size="small"
          rowKey="id"
          loading={acc.isLoading}
          dataSource={acc.data ?? []}
          pageSize={20}
          card={{ code: (a) => fmtDT(a.acceptedAt), title: (a) => <><Sensitive>{a.userName}</Sensitive>{a.supplierName ? ` · ${a.supplierName}` : ""}</>, subtitle: (a) => `${a.policyTitle} (${a.policyCode})`, fields: (a) => [["IP", a.ip ?? "—"]] }}
          columns={[
            { title: t("date"), dataIndex: "acceptedAt", render: (v: string) => fmtDT(v) },
            { title: t("user"), dataIndex: "userName", render: (v: string, a) => <span data-sensitive>{v}<br /><span style={{ fontSize: 12, opacity: 0.65 }}>{a.userEmail}</span></span> },
            { title: t("supplier"), dataIndex: "supplierName", render: (v: string | null) => v ?? "—" },
            { title: t("d_polTitle"), render: (_, a) => `${a.policyTitle} (${a.policyCode})` },
            { title: "IP", dataIndex: "ip", render: (v: string | null) => v ?? "—" },
          ]}
        />
      </Card>

      <Card
        title={t("d_retReportTitle")}
        extra={<Button size="small" loading={run.isPending} onClick={() => run.mutateAsync().then(() => void message.success(t("d_retRunDone"))).catch(fail)}>{t("d_retRun")}</Button>}
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t("d_retNoDelete")} />
        {!rr ? (
          <Typography.Text type="secondary">{t("d_retNever")}</Typography.Text>
        ) : (
          <>
            <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }} style={{ marginBottom: 12 }}>
              <Descriptions.Item label={t("d_retLastRun")}>{fmtDT(rr.ranAt)}</Descriptions.Item>
              <Descriptions.Item label={t("d_retEvidenceN")}>{rr.summary.evidenceVersions}</Descriptions.Item>
              <Descriptions.Item label={t("d_retLogN")}>{rr.summary.auditLogRows}</Descriptions.Item>
              <Descriptions.Item label={t("d_retAuditN")}>{rr.summary.closedAudits}</Descriptions.Item>
            </Descriptions>
            {rr.disabled.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={t("d_retDisabled", { n: rr.disabled.length })}
                description={<Sensitive>{rr.disabled.map((u) => `${u.name} (${u.email}) · ${t("d_retLastSeen")} ${fmtD(u.lastSeen)}`).join(" — ")}</Sensitive>}
              />
            )}
            {rr.truncated && <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>{t("d_retTruncated")}</Typography.Paragraph>}
            <ResponsiveList<RetentionItemDto>
              size="small"
              rowKey={(r) => `${r.kind}-${r.ref}`}
              dataSource={rr.items}
              pageSize={15}
              card={{ code: (r) => kindLabel(r.kind), title: (r) => r.label, fields: (r) => [[t("date"), fmtD(r.date)]] }}
              columns={[
                { title: t("d_retKind"), dataIndex: "kind", render: (v: RetentionItemDto["kind"]) => kindLabel(v) },
                { title: t("d_retItem"), dataIndex: "label" },
                { title: t("date"), dataIndex: "date", render: (v: string) => fmtD(v) },
              ]}
            />
          </>
        )}
      </Card>

      <Modal open={!!edit} onCancel={() => setEdit(null)} onOk={() => form.submit()} okText={t("save")} cancelText={t("cancel")} confirmLoading={mut.create.isPending || mut.update.isPending} {...modalProps} title={edit?.p ? t("d_polEdit") : t("d_polNew")} destroyOnHidden>
        <Form form={form} layout="vertical" onFinish={submit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 16 }}>
            <Form.Item name="code" label={t("d_polCode")} rules={[{ required: true, message: t("required") }]} extra={t("d_polCodeHelp")}><Input placeholder="informativa-fornitori-v1" /></Form.Item>
            <Form.Item name="audience" label={t("d_polAudience")}><Select options={[{ value: "supplier", label: t("d_aud_supplier") }, { value: "internal", label: t("d_aud_internal") }]} /></Form.Item>
          </div>
          <Form.Item name="title" label={t("title")} rules={[{ required: true, message: t("required") }]}><Input /></Form.Item>
          <Form.Item name="body" label={t("d_polBody")} rules={[{ required: true, message: t("required") }]}><Input.TextArea rows={14} /></Form.Item>
        </Form>
      </Modal>
      <Modal open={!!view} onCancel={() => setView(null)} footer={null} {...viewProps} title={view ? `${view.title} · ${view.code}` : ""} destroyOnHidden>
        <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{view?.body}</div>
      </Modal>
    </div>
  );
}
